import { type Context, createContext, type ReactNode, useContext } from 'react';

import type { DialogId } from '../analytics/types';
import type {
	Action,
	ActionId,
	AutomationFreeLimitStatus,
	Edition,
	Environment,
	Flag,
	LastKnownTrial,
	PremiumWrapperProps,
	Product,
	UserRole,
} from '../types';

/* eslint-disable perfectionist/sort-interfaces */
export interface EditionAwarenessContextType {
	//  ------------------------- Required properties -------------------------
	cloudId: string;
	product: Product;
	edition: Edition;

	//  ------------------------- Payment properties --------------------------
	/**
	 * If this is null, then we cannot say that the entitlement is invoiceable. Please consider not showing the experience to customers
	 * in this case as they may have payment details already.
	 */
	invoiceableStatus: 'INVOICEABLE' | 'NOT_INVOICEABLE' | 'UNKNOWN';
	canAddPaymentDetails?: boolean | null;
	hasPaymentDetailsEntered?: boolean;
	addPaymentDetailsExperienceUrl?: string | null;

	//  --------------------------- Trial properties ---------------------------
	trialDaysLeft?: number;
	trialLengthDays?: number;
	trialStartDate?: Date;
	trialEndDate?: Date;
	isInTrial?: boolean;
	isInPredunning?: boolean;
	isInReverseTrial?: boolean;
	trialOfferingName?: Edition;
	/** Only exists for CCP entitlements due to HAMs not supporting it */
	offeringKey?: string | null;
	/**
	 * This is the last known trial the customer was on, it's not guaranteed to be the current trial they are on.
	 */
	lastKnownTrial?: LastKnownTrial;

	//  ------------------------- Upgrade properties --------------------------
	canUpgrade?: boolean;
	canDirectBuyUpgrade?: boolean;
	changeOfferingToStandardExperienceUrl?: string | null;
	directBuyChangeOfferingToStandardExperienceUrl?: string | null;

	//  ---------------------- Collection properties ----------------------
	isInCollection?: boolean;
	collectionType?: string | null;
	collectionEntitlementId?: string | null;

	//  ---------------------- User properties ----------------------
	userId?: string | null;
	userDisplayName?: string | null;
	userRoles?: UserRole[];
	userEmail?: string | null;
	/** Will return true if we don't know it's false, to prevent auto-opening when the user has already seen it */
	hasDialogAutoOpened: (dialogId: DialogId) => boolean;

	//  ---------------------------- Site properties ----------------------------
	orgId?: string;
	navVersion?: string;
	environment?: Environment;
	licensedUsers: number;
	storageUsageInGB?: number;
	storageLimitInGB?: number;
	storageIsUnlimited?: boolean;
	entitlementId?: string | null;
	transactionAccountId?: string | null;
	isEnterpriseCustomer?: boolean;

	//  ---------------------------- Site traits ----------------------------
	automationUsage?: number;
	lastAutomationFreeLimitBreachDate?: Date | null;
	automationFreeLimitStatus?: AutomationFreeLimitStatus;
	isSiteEligibleToServcoUplift?: boolean;

	//  ------------------------- Capabilities  -------------------------
	//  ---- Dismissable button ----
	hasDismissedButton?: boolean;
	onDismissButton?: () => void;

	//  ---- RT Dropdown Premium Features ----
	onOpenOnboardingMenu?: () => void;
	premiumFeatureHighlightToggle?: ReactNode;
	shouldAutoOpen?: boolean;
	onAutoOpen?: () => void;
	/**
	 * Confluence in premium has a lot of specific packages that are wired up with the button,
	 * we're therefore passing it through to edition awareness to prevent fragmenting the implementation
	 * of those features (e.g. needing to replicate/shift the logic for button border nudges into platform)
	 */
	PremiumWrapper?: (props: PremiumWrapperProps) => ReactNode;

	/**
	 * Starts the async process to check if the customer has upgraded, this will eventually show flags to the customer
	 * (e.g. informing them, they have been upgraded and it's being processed).
	 *
	 * Returns if the customer has upgraded of not (times out after a period and assumes the customer did not upgrade)
	 */
	startCheckingForAttemptToUpgrade: () => Promise<boolean>;

	/**
	 * Get the in product action associated with the action id, actions are likely to be product specific,
	 * for example there's no concept of the action `whiteboards` in JSM
	 */
	getAction?: (id: ActionId) => Promise<Action<ActionId> | null>;

	/**
	 * Calls the in-product flag provider to show the flag to the customer
	 */
	showFlag?: (flag: Flag) => void;
	/**
	 * Dismisses a flag from in-product
	 */
	hideFlag?: (id: string) => void;

	/** is enrolled in the upgrade free pool target experiment */
	isEnrolledInUpgradeFreePoolTargetExperiment?: boolean;
}
/* eslint-enable perfectionist/sort-interfaces */

export const EditionAwarenessContext: Context<EditionAwarenessContextType | null> =
	createContext<EditionAwarenessContextType | null>(null);

export function useEditionAwareness(): EditionAwarenessContextType {
	const context = useContext(EditionAwarenessContext);

	if (!context) {
		throw new Error('Missing context, please ensure this is used within EditionAwarenessContext');
	}

	return context;
}
